import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import Loader from "../../components/UI/Loader";
import EmptyState from "../../components/UI/EmptyState";
import {
  getAlumnosSeguimientoService,
  getPlantelesAlumnosService,
} from "../../services/seguimiento/seguimiento.service";

const AlumnosSeguimientoPage = () => {
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");

  // Planteles disponibles según el rol.
  useEffect(() => {
    (async () => {
      try {
        const data = await getPlantelesAlumnosService();
        setPlanteles(data);
      } catch {
        // No es crítico; el listado sigue funcionando sin filtro.
      }
    })();
  }, []);

  // Listado con debounce.
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getAlumnosSeguimientoService({
          search: search.trim() || undefined,
          plantelId: plantelId || undefined,
        });
        setAlumnos(data);
      } catch {
        toast.error("Error al cargar los alumnos");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, plantelId]);

  // El selector aparece siempre que exista al menos un plantel visible al usuario.
  const mostrarSelectorPlantel = planteles.length > 0;

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-24">
          <div>
            <h4 className="mb-1">Mis alumnos</h4>
            <p className="text-secondary-light mb-0">
              Estudiantes con un mentor asignado.
            </p>
          </div>
        </div>

        <div className="row mb-3 g-3">
          <div className={mostrarSelectorPlantel ? "col-md-7 col-12" : "col-12"}>
            <div className="icon-field">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="mdi:magnify" />
              </span>
              <input
                type="text"
                className="form-control h-48-px bg-neutral-50 radius-12"
                placeholder="Buscar por nombre, apellido o correo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {mostrarSelectorPlantel && (
            <div className="col-md-5 col-12">
              <div className="icon-field">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="mdi:office-building-outline" />
                </span>
                <select
                  className="form-select h-48-px bg-neutral-50 radius-12"
                  value={plantelId}
                  onChange={(e) => setPlantelId(e.target.value)}
                >
                  <option value="">Todos los planteles</option>
                  {planteles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                      {p.escuela_nombre ? ` — ${p.escuela_nombre}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <Loader center text="Cargando alumnos..." delay={200} />
        ) : alumnos.length === 0 ? (
          <EmptyState
            icon="mdi:account-search-outline"
            title="No hay alumnos"
            description={
              search || plantelId
                ? "No encontramos alumnos con esos filtros."
                : "Aún no tienes alumnos con mentor asignado."
            }
          />
        ) : (
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th>Correo</th>
                  <th>Plantel / Escuela</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {alumnos.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: 36,
                            height: 36,
                            background: "rgba(99,102,241,0.12)",
                            color: "#6366f1",
                            fontWeight: 700,
                          }}
                        >
                          {a.nombre_completo?.[0] || "?"}
                        </div>
                        <span className="fw-semibold">{a.nombre_completo}</span>
                      </div>
                    </td>
                    <td className="text-secondary-light">{a.email}</td>
                    <td>
                      <span className="d-block text-sm fw-semibold">
                        {a.plantel_nombre || "—"}
                      </span>
                      <span className="text-xs text-secondary-light">
                        {a.escuela_nombre || "—"}
                      </span>
                    </td>
                    <td className="text-end">
                      <Link
                        to={`/mis-alumnos/${a.id}`}
                        className="btn btn-sm btn-primary rounded-pill px-3 d-inline-flex align-items-center gap-1"
                      >
                        <Icon icon="mdi:eye-outline" width="16" />
                        Ver detalle
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumnosSeguimientoPage;
